import { ActionContext } from "vuex";

import Api from "@/api/Api";
import { getProjectsApi } from "@/api/ProjectsApi";
import { createAllSelection, hasAllValue, prependAllSelection } from "@/helpers/all";
import { normalizeScenarioSet } from "@/helpers/projectOutputFilters";
import { collapseTraditionalViabilityToV2, expandTraditionalViabilityV2 } from "@/helpers/traditionalViability";
import Lang from "@/lang";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { ProjectOutputFilterParams, defaultPlanOutputFilterValues } from "@/settings/projectOutputFilters";
import store, { RootState } from "@/store";
import { ContentWidget, FullLayout, ItemLayout, Layout } from "@/types/layouts";
import { BudgetInTimeItem, BusinessGoal, OutputRecord, Plan, Project, Scenario, ScenarioOutput } from "@/types/project";
import { RecalculatePlanInput } from "@/types/tables";

export interface ProjectOutputState {
  project: Project | null;
  projectObjetChanged: boolean;
  projectFiltered: Project | null;
  projectOutputLayout: Layout | null;
  projectOutputLayoutInitial: FullLayout | null;
  isLoading: boolean;
  isLoadingArtificial: boolean;
  filters: ProjectOutputFilterParams;
  activeWidget: ContentWidget;
  showDrawerConfig: boolean;
}

const state: ProjectOutputState = {
  project: null,
  projectObjetChanged: false,
  projectFiltered: null,
  projectOutputLayout: [] as Layout,
  projectOutputLayoutInitial: {} as FullLayout,
  isLoading: false,
  isLoadingArtificial: false,
  filters: { ...defaultPlanOutputFilterValues },
  activeWidget: {} as ContentWidget,
  showDrawerConfig: false,
};

type Dist = { start_date: string; end_date: string; daily_budget: number };

function uniqBy<T>(arr: T[], key: keyof T): T[] {
  const seen = new Set();
  return arr.filter((item) => {
    const val = item[key];
    if (seen.has(val)) return false;
    seen.add(val);
    return true;
  });
}
const norm = (v: unknown) =>
  String(v ?? "")
    .trim()
    .toLowerCase();
const same = (a: unknown, b: unknown) => norm(a) === norm(b);

const isAll = (arr: string[]) => !Array.isArray(arr) || arr.length === 0 || hasAllValue(arr);

function clampToScenario(d: Dist, scenStart: string, scenEnd: string): Dist | null {
  const s = d.start_date < scenStart ? scenStart : d.start_date;
  const e = d.end_date > scenEnd ? scenEnd : d.end_date;
  if (e < s) return null;
  return { start_date: s, end_date: e, daily_budget: d.daily_budget };
}
function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = Date.UTC(y, m - 1, d) + days * 86400000;
  const dt = new Date(t);
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${mm}-${dd}`;
}
function normalizeDistribution(raw: Dist[], scenStart: string, scenEnd: string): Dist[] {
  const filtered = raw
    .filter((r) => (r?.daily_budget ?? 0) > 0)
    .map((r) => clampToScenario(r, scenStart, scenEnd))
    .filter((r): r is Dist => !!r);

  filtered.sort((a, b) => a.start_date.localeCompare(b.start_date));

  const merged: Dist[] = [];
  for (const cur of filtered) {
    const prev = merged[merged.length - 1];
    if (prev && prev.daily_budget === cur.daily_budget && addDays(prev.end_date, 1) === cur.start_date) {
      prev.end_date = cur.end_date;
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

export default {
  namespaced: true,
  state,

  mutations: {
    setProject(state: ProjectOutputState, project: Project | null) {
      state.project = project;
    },
    setProjectObjetChanged(state: ProjectOutputState, isChanged: boolean) {
      state.projectObjetChanged = isChanged;
    },
    setProjectFiltered(state: ProjectOutputState, project: Project | null) {
      state.projectFiltered = project;
    },
    setLoading(state: ProjectOutputState, val: boolean) {
      state.isLoading = val;
    },
    setLoadingArtificial(state: ProjectOutputState, val: boolean) {
      state.isLoadingArtificial = val;
    },
    setFilters(state: ProjectOutputState, filters: ProjectOutputFilterParams) {
      state.filters = filters;
    },
    setProjectOutputLayout(state: ProjectOutputState, projectOutputLayout: Layout) {
      state.projectOutputLayout = projectOutputLayout;
    },
    setProjectOutputLayoutInitial(state: ProjectOutputState, projectOutputLayout: FullLayout) {
      state.projectOutputLayoutInitial = projectOutputLayout;
    },
    updateSelectWidget(state: ProjectOutputState, item: ContentWidget) {
      state.activeWidget = item;
    },
    updateShowDrawerConfig(state: ProjectOutputState, value: boolean): void {
      state.showDrawerConfig = value;
    },
    deleteWidget(state: ProjectOutputState, widgetId: string) {
      if (!Array.isArray(state.projectOutputLayout)) return;
      const idx = state.projectOutputLayout.findIndex((item: ItemLayout) => item?.content?.id === widgetId);
      if (idx !== -1) state.projectOutputLayout.splice(idx, 1);
    },
  },

  actions: {
    async fetchExploreReportPreview(_: ActionContext<ProjectOutputState, RootState>, body: Record<string, unknown>) {
      try {
        return await Api.request<unknown>({
          path: API_ROUTES.REPORT.PATH,
          defaultResult: { data: [] },
          method: "post",
          body,
          token: requestTokens.apiServiceLoadReport,
        });
      } catch (error) {
        console.error("[projectOutput] failed to load explore report preview", error);
        throw error;
      }
    },
    async updateBudgetInTime(
      ctx: ActionContext<ProjectOutputState, RootState>,
      patches: Array<{
        scenario_id: string;
        kpi: string;
        channel: string;
        distribution: Array<{ start_date: string; end_date: string; daily_budget: number }>;
      }>
    ) {
      const { state, commit, dispatch } = ctx;
      if (!state.project || !Array.isArray(patches) || patches.length === 0) return;

      commit("setLoading", true);
      try {
        const next = JSON.parse(JSON.stringify(state.project));

        const byScenario = new Map<string, typeof patches>();
        for (const p of patches) {
          if (!byScenario.has(p.scenario_id)) byScenario.set(p.scenario_id, []);
          byScenario.get(p.scenario_id)?.push(p);
        }

        for (const [scenarioId, list] of byScenario) {
          let targetScenario: Scenario | undefined;
          outer: for (const bg of next.business_goals ?? []) {
            for (const pl of bg.plans ?? []) {
              for (const sc of pl.scenarios ?? []) {
                if (String(sc.id) === String(scenarioId)) {
                  targetScenario = sc;
                  break outer;
                }
              }
            }
          }
          if (!targetScenario) {
            console.warn("[updateBudgetInTime] scenario not found:", scenarioId);
            continue;
          }

          if (!targetScenario.output) targetScenario.output = {} as unknown as ScenarioOutput;
          if (!Array.isArray(targetScenario.output.budget_in_time)) {
            targetScenario.output.budget_in_time = [];
          }

          const scenStart = String(targetScenario.start_date);
          const scenEnd = String(targetScenario.end_date);

          const bucket: Array<{ kpi: string; channel: string; distribution: Dist[] }> = [];

          for (const p of list) {
            const normDist = normalizeDistribution(p.distribution, scenStart, scenEnd);
            bucket.push({ kpi: p.kpi, channel: p.channel, distribution: normDist });
          }

          const arr: BudgetInTimeItem[] = targetScenario.output.budget_in_time;
          for (const entry of bucket) {
            const idx = arr.findIndex((x) => x.kpi === entry.kpi && x.channel === entry.channel);
            if (idx === -1) {
              arr.push(entry);
            } else {
              arr[idx] = { ...arr[idx], distribution: entry.distribution };
            }
          }
        }

        commit("setProject", next);
        commit("setProjectObjetChanged", true);
        const activeFilters = (ctx.rootGetters && ctx.rootGetters["projectLayoutEdit/filterParams"]) || state.filters;
        await dispatch("filterProject", activeFilters);
      } finally {
        commit("setLoading", false);
      }
    },
    async recalculatePlan(ctx: ActionContext<ProjectOutputState, RootState>, payload: RecalculatePlanInput) {
      const { state, commit, dispatch } = ctx;
      if (!state.project) return;

      commit("setLoading", true);
      try {
        const resp = await getProjectsApi().postUpdatePlan(payload);

        if (!resp?.metrics_rows?.length) {
          store.commit("app/showAlert", { message: Lang.errorRecalculateTooLarge, type: "error" });
          return;
        }

        const next = JSON.parse(JSON.stringify(state.project));

        const findScenario = (sid: string): Scenario | undefined => {
          for (const bg of next.business_goals ?? []) {
            for (const pl of bg.plans ?? []) {
              for (const sc of pl.scenarios ?? []) {
                if (String(sc.id) === sid) return sc;
              }
            }
          }
          return undefined;
        };

        const rowsByScenario = new Map<string, typeof resp.metrics_rows>();
        for (const row of resp?.metrics_rows ?? []) {
          const sid = String(row.scenario_id ?? "");
          if (!sid) continue;
          if (!rowsByScenario.has(sid)) rowsByScenario.set(sid, []);
          rowsByScenario.get(sid)?.push(row);
        }

        for (const [sid, rows] of rowsByScenario) {
          const targetScenario = findScenario(sid);
          if (!targetScenario || !Array.isArray(targetScenario.output?.data)) {
            console.warn("[recalculatePlan] scenario not found or has no output.data for id=", sid);
            continue;
          }

          const index = new Map<string, number>();
          targetScenario.output?.data.forEach((x: OutputRecord, i: number) => {
            if (x.id) index.set(String(x.id), i);
          });

          for (const row of rows) {
            const flightId = String(row.id ?? "");
            const i = flightId ? index.get(flightId) : undefined;
            if (i !== undefined) {
              targetScenario.output?.data.splice(i, 1, {
                ...targetScenario.output?.data[i],
                ...row,
              } as OutputRecord);
            } else {
              console.warn("[recalculatePlan] flight id not found in scenario; appending:", flightId, row);
              const maybeData = targetScenario.output?.data;
              if (Array.isArray(maybeData)) {
                maybeData.push(row as OutputRecord);
                if (flightId) index.set(flightId, maybeData.length - 1);
              }
            }
          }
        }

        const budgetByScenario = new Map<string, BudgetInTimeItem[]>();
        for (const item of resp?.budget_in_time ?? []) {
          const sid = String(item?.scenario_id ?? "");
          if (!sid) continue;
          if (!budgetByScenario.has(sid)) budgetByScenario.set(sid, []);
          budgetByScenario.get(sid)?.push(item);
        }

        for (const [sid, items] of budgetByScenario) {
          const targetScenario = findScenario(sid);
          if (!targetScenario?.output) continue;

          const prevBudget = Array.isArray(targetScenario.output.budget_in_time)
            ? [...targetScenario.output.budget_in_time]
            : [];
          const incomingMap = new Map<string, BudgetInTimeItem>();
          for (const item of items) {
            const key = `${norm(item?.kpi)}|${norm(item?.channel)}`;
            if (!incomingMap.has(key)) incomingMap.set(key, item);
          }
          if (!incomingMap.size) continue;

          const nextBudget: BudgetInTimeItem[] = prevBudget.map((entry) => {
            const key = `${norm(entry?.kpi)}|${norm(entry?.channel)}`;
            const patch = incomingMap.get(key);
            if (!patch) return entry;

            incomingMap.delete(key);
            return {
              ...entry,
              kpi: patch.kpi ?? entry.kpi,
              channel: patch.channel ?? entry.channel,
              distribution: Array.isArray(patch.distribution) ? patch.distribution : [],
            };
          });

          for (const patch of incomingMap.values()) {
            nextBudget.push({
              scenario_id: sid,
              kpi: String(patch?.kpi ?? ""),
              channel: String(patch?.channel ?? ""),
              distribution: Array.isArray(patch?.distribution) ? patch.distribution : [],
            });
          }

          targetScenario.output.budget_in_time = nextBudget;
        }

        for (const entry of resp?.status_summary ?? []) {
          const targetScenario = findScenario(String(entry?.scenario_id ?? ""));
          if (targetScenario?.output) {
            targetScenario.output.status_summary = Array.isArray(entry?.messages) ? entry.messages : [];
          }
        }

        const mergeByRowKey = <T extends { row_key?: string }>(
          existing: T[] | null | undefined,
          incoming: T[]
        ): T[] => {
          const byKey = new Map<string, T>();
          for (const r of Array.isArray(existing) ? existing : []) byKey.set(String(r?.row_key ?? ""), r);
          for (const r of Array.isArray(incoming) ? incoming : []) byKey.set(String(r?.row_key ?? ""), r);
          return Array.from(byKey.values());
        };

        for (const upd of resp?.traditional_output_updates ?? []) {
          const targetScenario = findScenario(String(upd?.scenario_id ?? ""));
          if (!targetScenario?.output) continue;

          targetScenario.output.traditional_grp_week_rows = mergeByRowKey(
            targetScenario.output.traditional_grp_week_rows,
            upd.traditional_grp_week_rows ?? []
          );

          const mergedViability = mergeByRowKey(
            expandTraditionalViabilityV2(targetScenario.output.traditional_viability_v2),
            upd.traditional_viability_rows ?? []
          );
          targetScenario.output.traditional_viability_v2 = collapseTraditionalViabilityToV2(
            String(upd.scenario_id ?? ""),
            mergedViability
          );

          if (typeof upd.output_timestamp === "number") {
            targetScenario.output.timestamp = upd.output_timestamp;
          }
        }

        commit("setProject", next);
        commit("setProjectObjetChanged", true);

        const activeFilters = (ctx.rootGetters && ctx.rootGetters["projectLayoutEdit/filterParams"]) || state.filters;

        await dispatch("filterProject", activeFilters);
        return resp;
      } catch (e) {
        console.error("[projectOutput/recalculatePlan] error", e);
        store.commit("app/showAlert", { message: Lang.errorFailedToRecalculatePlan, type: "error" });
        throw e;
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchProjectById(ctx: ActionContext<ProjectOutputState, RootState>, projectId: string) {
      ctx.commit("setLoading", true);
      try {
        const project = await getProjectsApi().getProject(projectId);
        ctx.commit("setProject", project);
        ctx.commit("setProjectFiltered", project);

        let businessGoalValues: string[] = createAllSelection();
        let planValues: string[] = createAllSelection();
        let mediaTypeValues: string[] = createAllSelection();

        if (Array.isArray(project?.business_goals)) {
          businessGoalValues = prependAllSelection(
            project.business_goals
              .map((goal: BusinessGoal) => goal.name ?? goal.id ?? "")
              .filter((v: string | null | undefined): v is string => typeof v === "string" && v.length > 0)
          );

          const allPlans: Plan[] = project.business_goals.flatMap((goal: BusinessGoal) => goal.plans || []);
          const uniquePlans = uniqBy(allPlans, "id");
          planValues = prependAllSelection(uniquePlans.map((plan) => plan.name ?? plan.id ?? "").filter(Boolean));

          const allScenarios: Scenario[] = allPlans.flatMap((plan: Plan) => plan.scenarios || []);
          const allMediaTypes = allScenarios
            .flatMap((sc: Scenario) =>
              Array.isArray(sc.output?.data) ? sc.output?.data.map((row: OutputRecord) => row.media_type) : []
            )
            .filter((x): x is string => typeof x === "string" && x.length > 0);
          mediaTypeValues = prependAllSelection(Array.from(new Set(allMediaTypes)));
        }

        const scenarioTree: Array<{ id: string; label: string; children: Array<{ id: string; label: string }> }> = (
          project?.business_goals ?? []
        ).flatMap((bg: BusinessGoal) =>
          (bg.plans ?? []).map((pl: Plan) => ({
            id: String(pl.id),
            label: pl.name || "Plan",
            children: (pl.scenarios ?? []).map((sc: Scenario, idx: number) => ({
              id: String(sc.id),
              label: (sc.name && sc.name.trim()) || `Scenario ${idx + 1}`,
            })),
          }))
        );

        const newFilters: ProjectOutputFilterParams = {
          ...defaultPlanOutputFilterValues,
          businessGoalPropertyValue: businessGoalValues,
          plan: planValues,
          mediaType: mediaTypeValues,
          scenario: scenarioTree,
        };
        ctx.commit("setFilters", newFilters);

        return project;
      } catch (error) {
        ctx.commit("setProject", null);
        ctx.commit("setProjectFiltered", null);
        ctx.commit("setFilters", { ...defaultPlanOutputFilterValues });
        throw error;
      } finally {
        ctx.commit("setLoading", false);
      }
    },

    async backgroundFetchProjectById(ctx: ActionContext<ProjectOutputState, RootState>, projectId: string) {
      try {
        return await getProjectsApi().getProject(projectId);
      } catch (error) {
        console.warn("[backgroundFetchProjectById] failed:", error);
      }
    },

    filterProject(ctx: ActionContext<ProjectOutputState, RootState>, filters: ProjectOutputFilterParams) {
      ctx.commit("setLoading", true);
      const { project } = ctx.state;
      if (!project) return;

      const businessGoalsFilter = filters.businessGoalPropertyValue || [];
      const planFilter = filters.plan || [];
      const mediaTypeFilter = filters.mediaType || [];

      const scenarioIdSet = normalizeScenarioSet(filters.scenario);

      const filteredBusinessGoals = (project.business_goals || [])
        .filter(
          (goal: BusinessGoal) =>
            isAll(businessGoalsFilter) || businessGoalsFilter.some((v) => same(v, goal.name) || same(v, goal.id))
        )
        .map((goal: BusinessGoal) => {
          const filteredPlans = (goal.plans || [])
            .filter((p: Plan) => isAll(planFilter) || planFilter.some((v) => same(v, p.name) || same(v, p.id)))
            .map((p) => {
              const filteredScenarios = (p.scenarios || [])
                .filter((s) => !scenarioIdSet || scenarioIdSet.has(String(s.id)))
                .map((s: Scenario) => {
                  const filteredOutput = s.output
                    ? {
                        ...s.output,
                        data: (s.output.data || []).filter(
                          (entry: OutputRecord) =>
                            isAll(mediaTypeFilter) || mediaTypeFilter.some((v) => same(v, entry.media_type))
                        ),
                      }
                    : null;
                  return { ...s, output: filteredOutput };
                });

              return { ...p, scenarios: filteredScenarios };
            })
            .filter((p: Plan) => (p.scenarios || []).length > 0 || scenarioIdSet === null);

          return { ...goal, plans: filteredPlans };
        })
        .filter((g: BusinessGoal) => (g.plans || []).length > 0 || scenarioIdSet === null);

      const filteredProject = { ...project, business_goals: filteredBusinessGoals };
      ctx.commit("setProjectFiltered", filteredProject);
      setTimeout(() => ctx.commit("setLoading", false), 1000);
    },
    saveFilters(ctx: ActionContext<ProjectOutputState, RootState>, filters: ProjectOutputFilterParams) {
      ctx.commit("setFilters", filters);
    },
  },

  getters: {
    project(state: ProjectOutputState) {
      return state.project;
    },
    projectFiltered(state: ProjectOutputState) {
      return state.projectFiltered;
    },
    projectOutputLayout(state: ProjectOutputState) {
      return state.projectOutputLayout;
    },
    projectOutputLayoutInitial(state: ProjectOutputState) {
      return state.projectOutputLayoutInitial;
    },
    isLoading(state: ProjectOutputState) {
      return state.isLoading;
    },
    isLoadingArtificial(state: ProjectOutputState) {
      return state.isLoadingArtificial;
    },
    filters(state: ProjectOutputState) {
      return state.filters;
    },
    activeWidget: (state: ProjectOutputState): ContentWidget => {
      return state.activeWidget;
    },
    isShowDrawerConfig: (state: ProjectOutputState): boolean => {
      return state.showDrawerConfig;
    },
    isProjectObjectChanged: (state: ProjectOutputState): boolean => {
      return state.projectObjetChanged;
    },
  },
};
